import { Box, createIcon, Flex, Skeleton } from '@chakra-ui/react';
import { CardPromoInfo } from '@diamond/shared/ui';
import { trackEvent } from '@diamond/shared/utils';
import { TRACKING_EVENTS, TRACKING_PAGES } from '@diamond/sol-buyer/constant';
import { ProcessedPromo } from '@diamond/sol-buyer/utils';
import Link from 'next/link';

const ChevronRightIcon = createIcon({
  displayName: 'ChevronRightIcon',
  viewBox: '0 0 20 20',
  path: (
    <path
      d="M10.4998 10L6.6665 6.16667L7.83317 5L12.8332 10L7.83317 15L6.6665 13.8333L10.4998 10Z"
      fill="currentColor"
    />
  ),
});

type PromoInfoListProductDetailProps = {
  processedPromo: ProcessedPromo;
  isFetching?: boolean;
  section?: string;
};

export function PromoInfoListProductDetail({
  processedPromo,
  isFetching,
  section = 'undefined',
}: PromoInfoListProductDetailProps) {
  if (isFetching) {
    return (
      <Skeleton
        height="90px"
        borderTopLeftRadius="4px"
        borderTopRightRadius="12px"
        borderBottomRadius="4px"
      />
    );
  }

  const {
    discount,
    bundledDiscount,
    cashback,
    bundledCashback,
    freeGoods,
    bundledFreeGoods,
    hasFreeGoods,
    hasBundledFreeGoods,
    hasDiscount,
    hasBundledDiscount,
    hasCashback,
    hasBundledCashback,
  } = processedPromo;

  const shouldShowPromoInfoList =
    hasFreeGoods ||
    hasDiscount ||
    hasCashback ||
    hasBundledCashback ||
    hasBundledDiscount ||
    hasBundledFreeGoods;

  return shouldShowPromoInfoList ? (
    <Box>
      <Flex flexDirection="column" gap="4" mb="16px">
        {hasFreeGoods
          ? freeGoods.map((d) => (
              <Link
                key={d.promoId}
                href={`/promo/${d.promoId}`}
                style={{ textDecoration: 'none' }}
                onClick={() =>
                  trackEvent(TRACKING_EVENTS.CLICK_PROMO_DETAIL, {
                    promo_id: d.promoId,
                    name: d.text,
                    section,
                    category: 'free-item',
                    section_name: TRACKING_PAGES.PRODUCT_DETAIL,
                  })
                }
              >
                <CardPromoInfo
                  type="free-item"
                  duration={d.duration}
                  text={d.text}
                  item={d.item}
                  extra={
                    <Flex justify="center" align="center">
                      <ChevronRightIcon boxSize="20px" color="#0000A3" />
                    </Flex>
                  }
                />
              </Link>
            ))
          : null}
        {hasBundledFreeGoods
          ? bundledFreeGoods.map((d) => (
              <Link
                key={d.promoId}
                href={`/promo/${d.promoId}`}
                style={{ textDecoration: 'none' }}
                onClick={() =>
                  trackEvent(TRACKING_EVENTS.CLICK_PROMO_DETAIL, {
                    promo_id: d.promoId,
                    name: d.text,
                    section,
                    category: 'free-item',
                    section_name: TRACKING_PAGES.PRODUCT_DETAIL,
                  })
                }
              >
                <CardPromoInfo
                  type="free-item"
                  duration={d.duration}
                  text={d.text}
                  item={d.item}
                  extra={
                    <Flex justify="center" align="center">
                      <ChevronRightIcon boxSize="20px" color="#0000A3" />
                    </Flex>
                  }
                />
              </Link>
            ))
          : null}
        {hasDiscount
          ? discount.map((d) => (
              <Link
                key={d.promoId}
                href={`/promo/${d.promoId}`}
                style={{ textDecoration: 'none' }}
                onClick={() =>
                  trackEvent(TRACKING_EVENTS.CLICK_PROMO_DETAIL, {
                    promo_id: d.promoId,
                    name: d.text,
                    section,
                    category: 'discount',
                    section_name: TRACKING_PAGES.PRODUCT_DETAIL,
                  })
                }
              >
                <CardPromoInfo
                  type="discount"
                  duration={d.duration}
                  text={d.text}
                  extra={
                    <Flex justify="center" align="center">
                      <ChevronRightIcon boxSize="20px" color="#0000A3" />
                    </Flex>
                  }
                />
              </Link>
            ))
          : null}
        {hasBundledDiscount
          ? bundledDiscount.map((d) => (
              <Link
                key={d.promoId}
                href={`/promo/${d.promoId}`}
                style={{ textDecoration: 'none' }}
                onClick={() =>
                  trackEvent(TRACKING_EVENTS.CLICK_PROMO_DETAIL, {
                    promo_id: d.promoId,
                    name: d.text,
                    section,
                    category: 'discount',
                    section_name: TRACKING_PAGES.PRODUCT_DETAIL,
                  })
                }
              >
                <CardPromoInfo
                  type="discount"
                  duration={d.duration}
                  text={d.text}
                  extra={
                    <Flex justify="center" align="center">
                      <ChevronRightIcon boxSize="20px" color="#0000A3" />
                    </Flex>
                  }
                />
              </Link>
            ))
          : null}
        {hasCashback
          ? cashback.map((d) => (
              <Link
                key={d.promoId}
                href={`/promo/${d.promoId}`}
                style={{ textDecoration: 'none' }}
                onClick={() =>
                  trackEvent(TRACKING_EVENTS.CLICK_PROMO_DETAIL, {
                    promo_id: d.promoId,
                    name: d.text,
                    section,
                    category: 'cashback',
                    section_name: TRACKING_PAGES.PRODUCT_DETAIL,
                  })
                }
              >
                <CardPromoInfo
                  type="cashback"
                  duration={d.duration}
                  text={d.text}
                  extra={
                    <Flex justify="center" align="center">
                      <ChevronRightIcon boxSize="20px" color="#0000A3" />
                    </Flex>
                  }
                />
              </Link>
            ))
          : null}
        {hasBundledCashback
          ? bundledCashback.map((d) => (
              <Link
                key={d.promoId}
                href={`/promo/${d.promoId}`}
                style={{ textDecoration: 'none' }}
                onClick={() =>
                  trackEvent(TRACKING_EVENTS.CLICK_PROMO_DETAIL, {
                    promo_id: d.promoId,
                    name: d.text,
                    section,
                    category: 'cashback',
                    section_name: TRACKING_PAGES.PRODUCT_DETAIL,
                  })
                }
              >
                <CardPromoInfo
                  type="cashback"
                  duration={d.duration}
                  text={d.text}
                  extra={
                    <Flex justify="center" align="center">
                      <ChevronRightIcon boxSize="20px" color="#0000A3" />
                    </Flex>
                  }
                />
              </Link>
            ))
          : null}
      </Flex>
    </Box>
  ) : null;
}
